import { List, Map } from 'immutable'
import { Action, any, async, AsyncState, browserRouter, Fun, IOWidget, mk_pair, nothing, Option, Pair, some, Widget } from 'widgets-for-react'
import { unescapeHTML } from '../../utils'
import { ExtraParameters } from '../cms/router/type.gen'
import { AppState } from '../cms/state/types.gen'
import { EntryClient } from '../cms/widget.gen'
import {
  ContentType,
  emptyAppState, HeaderState,
  OptionsState, PublicSiteState
} from '../public_site.main.mod'
import { WordPressBlockImagesLoader } from '../wordpress/rendering/image_utils.mod'
import { CustomBlock } from '../wordpress/types_and_parser/custom_blocks.mod'
import { Breadcrumb } from '../wordpress/types_and_parser/extra_types.mod'
import { Block } from '../wordpress/types_and_parser/standard_blocks.gen'
import { ParsedNode } from '../wordpress/types_and_parser/types.ext.gen'
import { WordPress } from '../wordpress/wordpress_api'
import { WordPressBlock_To_WidgetBlock } from '../wordpress/wordpress_to_cms.gen'
import { CustomBlockData } from './blocks/custom_blocks/types.mod'
import { CustomBlock_To_Widget } from './CustomBlockToWidget'
import { CustomPages, PublicSiteCustomRoutes } from './custom_pages/types.mod'
import { history } from './templates/history.mod'
import { RegionSwitchState } from './templates/regionSwitch/regionSwitchState.mod'
import { sidenote } from './templates/sidenote.mod'
import { closeSearchAndMenu } from './utils/app.utils'
import { ExtraImages } from './utils/extra_images.mod'

export const availableLanguages: LanguageType[] = [
  { short: 'nl', full: 'Nederlands' },
  { short: 'en', full: 'English' },
  { short: 'de', full: 'German' },
  { short: 'ng', full: 'Nigerian' }
]

export type LanguageType = {
  short: string,
  full: string
}

export type PublicSite_CustomPageData = {
  posts: Map<number, AsyncState<WordPress.PostTypeResponse>>
  post: WordPress.PostTypeResponse
  latest_articles: AsyncState<Array<WordPress.PostTypeResponse>>
  news: WordPress.Overview<'news'>
  stories: WordPress.Overview<'story'>
  extra_params?: ExtraParameters
  acf?: any
}

export const application = (
  data: { rootpath: string; rootpath_to_ignore: string; login_address?: string },
  regionSwitcher: (_: RegionSwitchState) => Widget<Action<RegionSwitchState>>,
  header: (_: HeaderState, o: OptionsState) => Widget<Action<HeaderState>>,
  footer: (
    _: AppState<PublicSiteState, PublicSiteCustomRoutes>
  ) => Widget<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>,
  errorPage: IOWidget<PublicSiteState, never>,
  loadingPage: Widget<never>,
  page: Fun<Pair<List<Breadcrumb>, Option<ParsedNode>>, ContentType>,
  custom_pages: CustomPages
) => {
  let rootpath = data.rootpath ? data.rootpath : ''
  let rootpath_to_ignore = data.rootpath_to_ignore ? data.rootpath_to_ignore : ''

  return (s0: AppState<PublicSiteState, PublicSiteCustomRoutes>) => {
    return browserRouter<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>()(
      s0.route.kind == 'ignore'
        ? nothing()
        : any<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>()([
          async<WordPress.OptionsResponse>(_ => nothing())(s0.state.options).map<
            Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
          >(a => s => {
            let s1 = a(s.state.options)

            return s1.kind == 'loaded'
              ? {
                ...s0,
                state: {
                  ...s0.state,
                  options: s1
                }
              }
              : s0
          }),

          s0.state.page.page.kind == 'loaded'
            ? async<Array<WordPress.PostTypeResponse>>(_ => nothing())(
              s0.state.page.page.value.extra_data.latest_articles
            ).map<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>(a => s => {
              if (s.state.page.page.kind != 'loaded') return s

              return {
                ...s,
                state: {
                  ...s.state,
                  page: {
                    ...s.state.page,
                    page: {
                      ...s.state.page.page,
                      value: {
                        ...s.state.page.page.value,
                        extra_data: {
                          ...s.state.page.page.value.extra_data,
                          latest_articles: a(s.state.page.page.value.extra_data.latest_articles)
                        }
                      }
                    }
                  }
                }
              }
            })
            : nothing(),

          s0.state.page.page.kind == 'loaded' &&
            s0.state.page.page.value.extra_data.post.type == 'page' &&
            s0.state.page.page.value.extra_data.post.template == 'news-overview'
            ? async<WordPress.OverviewResponse>(_ => nothing())(s0.state.page.page.value.extra_data.news.async).map<
              Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
            >(a => s => {
              if (s.state.page.page.kind != 'loaded') return s

              return {
                ...s,
                state: {
                  ...s.state,
                  page: {
                    ...s.state.page,
                    page: {
                      ...s.state.page.page,
                      value: {
                        ...s.state.page.page.value,
                        extra_data: {
                          ...s.state.page.page.value.extra_data,
                          news: {
                            ...s.state.page.page.value.extra_data.news,
                            async: a(s.state.page.page.value.extra_data.news.async)
                          }
                        }
                      }
                    }
                  }
                }
              }
            })
            : nothing(),

          s0.state.page.page.kind == 'loaded' &&
            s0.state.page.page.value.extra_data.post.type == 'page' &&
            s0.state.page.page.value.extra_data.post.template == 'news-overview' &&
            s0.state.page.page.value.extra_data.news.kind == 'news'
            ? async<Array<number>>(_ => nothing())(s0.state.page.page.value.extra_data.news.years).map<
              Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
            >(a => s => {
              if (s.state.page.page.kind != 'loaded') return s

              return {
                ...s,
                state: {
                  ...s.state,
                  page: {
                    ...s.state.page,
                    page: {
                      ...s.state.page.page,
                      value: {
                        ...s.state.page.page.value,
                        extra_data: {
                          ...s.state.page.page.value.extra_data,
                          news: {
                            ...s.state.page.page.value.extra_data.news,
                            years: a(s.state.page.page.value.extra_data.news.kind == 'news' ? s.state.page.page.value.extra_data.news.years : null)
                          }
                        }
                      }
                    }
                  }
                }
              }
            })
            : nothing(),
          s0.state.page.page.kind == 'loaded' &&
            s0.state.page.page.value.extra_data.post.type == 'page' &&
            s0.state.page.page.value.extra_data.post.template == 'news-overview' &&
            s0.state.page.page.value.extra_data.news.kind == 'news'
            ? async<Array<WordPress.TaxonomyResponse>>(_ => nothing())(s0.state.page.page.value.extra_data.news.categories).map<
              Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
            >(a => s => {
              if (s.state.page.page.kind != 'loaded') return s

              return {
                ...s,
                state: {
                  ...s.state,
                  page: {
                    ...s.state.page,
                    page: {
                      ...s.state.page.page,
                      value: {
                        ...s.state.page.page.value,
                        extra_data: {
                          ...s.state.page.page.value.extra_data,
                          news: {
                            ...s.state.page.page.value.extra_data.news,
                            categories: a(s.state.page.page.value.extra_data.news.kind == 'news' ? s.state.page.page.value.extra_data.news.categories : null)
                          }
                        }
                      }
                    }
                  }
                }
              }
            })
            : nothing(),


          any<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>()(
            s0.state.page.page.kind == 'loaded'
              ? s0.state.page.page.value.extra_data.posts
                .map((p, key) =>
                  async<WordPress.PostTypeResponse>(_ => nothing())(p).map<
                    Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
                  >(a => s => {
                    if (s.state.page.page.kind != 'loaded') return s
                    let a1 = a(s.state.page.page.value.extra_data.posts.get(key))
                    return {
                      ...s,
                      state: {
                        ...s.state,
                        page: {
                          ...s.state.page,
                          page: {
                            ...s.state.page.page,
                            value: {
                              ...s.state.page.page.value,
                              extra_data: {
                                ...s.state.page.page.value.extra_data,
                                posts: s.state.page.page.value.extra_data.posts.set(key, a1)
                              }
                            }
                          }
                        }
                      }
                    }
                  })
                )
                .valueSeq()
                .toArray()
              : [nothing()]
          ),
          regionSwitcher(s0.state.regionSwitch).map<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>(
            a => s1 => ({
              ...s1,
              state: { ...s1.state, regionSwitch: a(s1.state.regionSwitch) }
            })
          ),
          header(s0.state.header, s0.state.options).map<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>(
            a => s1 => ({
              ...s1,
              state: { ...s1.state, header: a(s1.state.header) }
            })
          ),
          EntryClient<
            CustomBlockData,
            CustomBlock,
            Block,
            PublicSiteState,
            keyof CustomPages,
            PublicSiteCustomRoutes,
            ExtraImages,
            PublicSite_CustomPageData
          >({
            initState: emptyAppState(page),
            custom_pages,
            StandardBlockImagesLoader: WordPressBlockImagesLoader,
            StandardBlock_To_Widget: WordPressBlock_To_WidgetBlock,
            errorPage: errorPage,
            loadingPage: loadingPage,
            rootpath: rootpath,
            rootpath_to_ignore: rootpath_to_ignore,
            CustomBlock_To_Widget: CustomBlock_To_Widget,
            CustomBLockImagesLoader: (c: CustomBlock) => []
          })(s0),

          footer(s0).map<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>(a => s1 => a(s1)),
          history().map<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>(la => {
            la.action == 'PUSH' && window.scrollTo(0, 0)
            return closeSearchAndMenu
          }),
          sidenote<AppState<PublicSiteState, PublicSiteCustomRoutes>>(

          )(_ => nothing())(s0)
        ])
    ).map<AppState<PublicSiteState, PublicSiteCustomRoutes>>(a => {
      let s1 = a(s0)

      let maybe_header = s1.state.page.blocks.get(0)
      if (maybe_header != undefined && maybe_header.kind == 'headerBlock') {
        document.title = maybe_header.value.fst.title + " | FrieslandCampina Institute"
      }

      if (s1.state.page.page.kind == "loaded" && s0.state.page.page.kind != "loaded") {
        s1.state.page.display = page(mk_pair(List(), some(s1.state.page.page.value.extra_data.post)))
      }

      if (s0.route != s1.route) {
        s1 = closeSearchAndMenu(s1)
      }


      if (
        s1.state.page.page.kind == 'loaded' &&
        s1.state.page.page.value.extra_data.post.yoast &&
        s1.state.page.page.value.extra_data.post.yoast.title
      ) {
        document.title = unescapeHTML(s1.state.page.page.value.extra_data.post.yoast.title)
      }

      s1.state.header.menu == 'open' ?
        document.documentElement.classList.add('menu--open')
        : document.documentElement.classList.remove('menu--open')

      if (s1.state.header.menu == 'open' || s1.state.header.showSearchBar) {
        document.body.setAttribute('data-scroll', 'locked')
      } else {
        document.body.setAttribute('data-scroll', '')
      }

      if (s1.route != s0.route) {
        // document.getElementsByClassName("header")[0].scrollIntoView()
      }
      return s1
    })
  }
}
